import {
  ApplicationConfig,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { APP_VERSION } from './app-version';
import { Store, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideLibTranslations, ru } from 'ngx-mt-ui-lib/translate';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideToast, ToastConfig } from 'ngx-mt-ui-lib/services/toast';
import { AuthEffects } from './features/auth/auth.effects';
import { authReducer } from './features/auth/auth.reducer';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { authStateInitializer } from './core/app-initializers/auth.initializer';
import { PublicEffects } from './features/public/public.effects';
import { publicReducer } from './features/public/public.reducer';
import { publicStateInitializer } from './core/app-initializers/public-state.initializer';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { AccessService } from 'ngx-mt-ui-lib/services/access';
import { AscupAccessService } from './core/services/ascup-access.service';
import { IAppState } from './app.state';
import { environment } from '@env/environment';
import { selectCurrentUser } from './features/auth/auth.selectors';
import { LucideInfo, LucideTriangleAlert } from '@lucide/angular';
import {
  CHAT_API_CONFIG,
  CHAT_CONVERSATION_LINK_ADAPTER,
  CHAT_CONVERSATION_PRESENTATION_ADAPTER,
  CHAT_ENTITY_LINK_ADAPTER,
  CHAT_REFERENCE_SUGGESTION_SOURCE,
  CHAT_MENU_COMPOSITION_CONTRIBUTOR,
  CHAT_SYSTEM_NOTIFICATION_ADAPTER,
  ChatApiConfig,
  isChatApiAuthenticationFailure,
  provideMtChatClient,
} from 'ias-ppr-mt-chat-client';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import {
  APPLICATION_SESSION,
  ApplicationSession,
} from '@core/services/auth/application-session';
import { AscupChatConversationPresentationAdapter } from './features/chat-page/services/ascup-chat-conversation-presentation.adapter';
import { AscupChatConversationLinkAdapter } from './features/chat-page/services/ascup-chat-conversation-link.adapter';
import { AscupChatEntityLinkAdapter } from './features/chat-page/services/ascup-chat-entity-link.adapter';
import { AscupChatReferenceSuggestionSource } from './features/chat-page/services/ascup-chat-reference-suggestion.source';
import { PwaChatSystemNotificationAdapter } from './features/chat-page/services/pwa-chat-system-notification.adapter';
import { pwaChatNavigationInitializer } from './features/chat-page/services/pwa-chat-navigation.service';
import { pwaUpdateInitializer } from '@core/services/pwa-update.service';
import { metroMapPreloadInitializer } from '@core/services/metro-map-preload.service';
import { provideApiClient, provideReactiveApi } from 'ngx-mt-ui-lib/api-client';
import { PwaReactiveApiConfig } from './core/reactive-api/pwa-reactive-api.config';
import { APP_NOTIFICATION_BELL_ENABLED } from '@shared/components/notification-bell/notification-bell.component';
import { notificationPushNavigationInitializer } from '@core/services/notification-push-navigation.service';
import { nativePushPermissionInitializer } from '@core/app-initializers/native-push.initializer';
import { nativePresentationInitializer } from '@core/app-initializers/native-presentation.initializer';
import { pwaChatBackgroundRegistration } from './features/chat-page/services/pwa-chat-background-registration.factory';
import { pwaChatNotificationMenu } from './features/chat-page/services/pwa-chat-notification-menu.factory';

export const appToastConfig: Partial<ToastConfig> = {
  duration: 2000,
  icon: { icon: LucideInfo },
  summaryDisplay: 'wrap',
  error: {
    duration: 3000,
    icon: { icon: LucideTriangleAlert, color: 'var(--md-sys-color-error)' },
  },
};

export function createChatApiConfig(
  store: Store<IAppState>,
  session: ApplicationSession,
): ChatApiConfig {
  const session$ = combineLatest([
    session.state$,
    store.select(selectCurrentUser),
  ]).pipe(
    switchMap(([state, currentUser]) =>
      state.authenticated && currentUser
        ? from(session.accessToken()).pipe(
            map((accessToken) => ({
              accessToken,
              identityKey: session.subject,
            })),
            catchError(() => of(null)),
          )
        : of(null),
    ),
    distinctUntilChanged(
      (previous, current) =>
        previous?.accessToken === current?.accessToken &&
        previous?.identityKey === current?.identityKey,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  return {
    apiPath: environment.CHAT_API,
    realtimePath: environment.CHAT_WS,
    session: () => session$,
    isAuthenticationFailure: isChatApiAuthenticationFailure,
    authenticationFailed: ({ accessToken, reason }) => {
      if (reason === 'reauthenticationRequired') {
        session.rejectSession(accessToken);
        return;
      }

      void session.accessToken(accessToken).catch(() => undefined);
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withInMemoryScrolling({ anchorScrolling: 'enabled' }),
    ),
    provideStore({
      auth: authReducer,
      public: publicReducer,
    }),
    provideEffects(AuthEffects, PublicEffects),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideHttpClient(withInterceptors([tokenInterceptor])),
    provideServiceWorker('mt-chat-service-worker.js', {
      enabled: environment.enablePWA,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideApiClient({
      apiBasePath: environment.API_URL,
    }),
    provideReactiveApi(PwaReactiveApiConfig),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: `.json?v=${APP_VERSION}`,
      }),
      fallbackLang: 'ru',
    }),
    provideLibTranslations({
      ru: ru,
    }),
    provideToast(appToastConfig),
    provideMtChatClient({
      browserUnreadIndicator: { favicon: true, appBadge: false },
      systemNotificationBackgroundRegistration: pwaChatBackgroundRegistration,
    }),
    {
      provide: CHAT_MENU_COMPOSITION_CONTRIBUTOR,
      multi: true,
      useFactory: pwaChatNotificationMenu,
    },
    {
      provide: CHAT_API_CONFIG,
      deps: [Store, APPLICATION_SESSION],
      useFactory: createChatApiConfig,
    },
    {
      provide: CHAT_CONVERSATION_PRESENTATION_ADAPTER,
      useClass: AscupChatConversationPresentationAdapter,
    },
    {
      provide: CHAT_CONVERSATION_LINK_ADAPTER,
      useClass: AscupChatConversationLinkAdapter,
    },
    {
      provide: CHAT_ENTITY_LINK_ADAPTER,
      useClass: AscupChatEntityLinkAdapter,
    },
    {
      provide: CHAT_REFERENCE_SUGGESTION_SOURCE,
      useClass: AscupChatReferenceSuggestionSource,
    },
    {
      provide: CHAT_SYSTEM_NOTIFICATION_ADAPTER,
      useClass: PwaChatSystemNotificationAdapter,
    },
    provideAppInitializer(authStateInitializer),
    provideAppInitializer(publicStateInitializer),
    provideAppInitializer(pwaChatNavigationInitializer),
    provideAppInitializer(notificationPushNavigationInitializer),
    provideAppInitializer(nativePushPermissionInitializer),
    provideAppInitializer(nativePresentationInitializer),
    provideAppInitializer(pwaUpdateInitializer),
    provideAppInitializer(metroMapPreloadInitializer),
    {
      provide: AccessService,
      useClass: AscupAccessService,
    },
    { provide: APP_NOTIFICATION_BELL_ENABLED, useValue: true },
  ],
};
